import Header from '@/components/common/MainContentHeader';
import useI18n from '@/hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { useLocation } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import { useNavigate } from 'react-router-dom';
import CustomProgressBar from '@/components/common/CustomProgressBar';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';
import { getUsedPlatforms } from '@/utils/socialplatformUtils';
import { formatPercent, formatMoney } from '@/utils/numberUtils';
import Loading from '@/components/common/Loading';
import { formatCampaignDate, formatTimeFromISO } from '@/utils/dateUtils';
import Model from '@/components/common/models/Model';
import LinkShareCard from '@/components/LinkShareCard/LinkShare';
import IconButton from '@/components/common/IconButton';
import PausedCampaign from './PausedCampaign';
import DraftCampaign from './DraftCampaign';
import { BrandMemberRole } from '@/types/enums';
import ToolTip from '@/components/common/ToolTip';
import { generateSmartLink } from '@/utils/smartLinkUtils';
import { toCampaignShareLink } from '@/utils/campaignShareLinkUtils';
import {
  useGetBrandCampaignsInfiniteQuery,
  useGetCampaignStatsQuery,
  useUpdateCampaignMutation,
} from '@/hooks/queries/useCampaignQueries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queries/queryKeys';
import RefreshButton from '@/components/common/RefreshButton';
import { useForceRefresh } from '@/hooks/useForceRefresh';

const ActiveCampaign = () => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isActionLoading, setIsActionLoading] = useState(false);
  // Every tab here (Active, Paused, Draft) and the tab counts are ['campaigns']
  // queries; the list cards' engagement rate is cached server-side, so the
  // refetch runs under force-refresh (?refresh=true) to recompute it.
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    () =>
      queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'active' }),
    'campaign-list',
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copyLink, setCopyLink] = useState('');
  const [viewMode, setViewMode] = useState<'active' | 'paused' | 'draft'>(
    'active',
  );

  useEffect(() => {
    if (location.state?.tab === 'draft') {
      setViewMode('draft');
    } else if (location.state?.tab === 'paused') {
      setViewMode('paused');
    }
  }, [location.state]);

  const handleShareModalClose = () => setIsShareModalOpen(false);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;
  const brandId = user?.brand?._id || '';
  const isRealUser = user?.role === 'brandUser' && !!brandId;
  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const isRegularBrandUser = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.User,
    [user?.brandMemberRole],
  );

  const EmptyState: React.FC<{
    title: string;
    description: string;
    icon?: string;
  }> = ({ title, description, icon = '../assets/paper.png' }) => {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <img src={icon} className="h-12 w-12 opacity-50" />
        <h3 className="text-lg font-medium text-gray-700">{title}</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          {description}
        </p>
      </div>
    );
  };

  const {
    data: campaignsData,
    isLoading: isCampaignLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useGetBrandCampaignsInfiniteQuery(
    'live',
    viewMode === 'active' && isRealUser,
  );

  const { data: campaignStats } = useGetCampaignStatsQuery(brandId, isRealUser);

  const updateMutation = useUpdateCampaignMutation();

  const activeCampaigns =
    campaignsData?.pages.flatMap((page: any) => page?.campaigns ?? []) ?? [];

  const isLoading = isCampaignLoading || isActionLoading;
  const isInfiniteLoading = isFetchingNextPage;

  const handleShareModalOpen = (id: string) => {
    const campaign = activeCampaigns.find((campaign) => campaign._id === id);
    if (campaign && campaign.smartLinkId) {
      const smartLink = generateSmartLink(campaign.smartLinkId);
      setCopyLink(toCampaignShareLink(smartLink, campaign.status === 'live'));
    } else {
      setCopyLink('');
    }
    setIsShareModalOpen(true);
  };

  const pauseActiveCampaign = async (campaignId: string) => {
    setIsActionLoading(true);
    try {
      const response = await updateMutation.mutateAsync({
        id: campaignId,
        updateData: { status: 'paused' },
      });
      if (response) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.byStatusInfinite('live'),
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.campaigns.stats(brandId),
        });
      }
      toast.success(t('common.pauseSuccessMsg'));
    } catch (error) {
      toast.error(t('wallet.errors.spending'));
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return (
    <>
      <div className=" h-[calc(100%-152px)] background-white overflow-visible">
        <div className="flex flex-col p-[12px] sm:p-[24px] bg-[#F0F5FF] gap-6">
          <Header
            title=""
            subtitle={String(
              viewMode === 'active'
                ? t('common.activeCampaign')
                : viewMode === 'paused'
                  ? t('common.pausedCampaign')
                  : t('common.draftCampaigns'),
            )}
          />
          <div className="flex items-center justify-between w-full xs:w-auto">
            <div className="flex items-center gap-2">
              <Button
                variant="filled"
                className={`min-w-max mt-2 md:mt-0 md:ml-2 ml-0 ${
                  viewMode === 'active' ? 'bg-white' : 'bg-transparent'
                } text-base font-normal`}
                onClick={() => setViewMode('active')}
              >
                {t('common.active')} ({campaignStats?.live})
              </Button>
              <Button
                variant="filled"
                className={`min-w-max mt-2 md:mt-0 md:ml-2 ml-0 ${
                  viewMode === 'paused' ? 'bg-white' : 'bg-transparent'
                } text-base font-normal`}
                onClick={() => setViewMode('paused')}
              >
                {t('common.paused')} ({campaignStats?.paused})
              </Button>
              <Button
                variant="filled"
                className={`min-w-max mt-2 md:mt-0 md:ml-2 ml-0 ${
                  viewMode === 'draft' ? 'bg-white' : 'bg-transparent'
                } text-base font-normal`}
                onClick={() => setViewMode('draft')}
              >
                {t('common.draft')} (
                {campaignStats?.draft ? campaignStats?.draft : 0})
              </Button>
            </div>
            <RefreshButton
              onRefresh={refresh}
              isRefreshing={isRefreshing}
              secondsRemaining={secondsRemaining}
            />
          </div>
        </div>
        {viewMode === 'active' ? (
          !isLoading && activeCampaigns.length <= 0 ? (
            <EmptyState
              title={t('common.noData')}
              description={t('common.noActiveCampaignFound')}
            />
          ) : (
            activeCampaigns.map((campaign) => {
              const startDate = formatCampaignDate(campaign.startDate, false);
              const endDate = formatCampaignDate(campaign.endDate ?? '', false);
              const startTime = formatTimeFromISO(campaign.startDate);
              const platform = getUsedPlatforms(campaign.containType);
              const data = [
                {
                  label: t('common.budget'),
                  value: isDummyUser
                    ? formatMoney(10000)
                    : formatMoney(campaign.campaignBudget),
                },
                {
                  label: t('common.remainingBalance'),
                  // Derived: budget (10,000) − cashback paid (1,840) = 8,160.
                  value: isDummyUser
                    ? formatMoney(10000 - 1840)
                    : formatMoney(campaign.remainingCampaignBudget),
                },
                {
                  label: t('common.participant'),
                  value: isDummyUser ? 92 : campaign.totalParticipants,
                },
                {
                  label: t('common.engagement'),
                  value: isDummyUser
                    ? '8%'
                    : formatPercent(campaign.engagementRate),
                },
                // {
                //   label: t('common.earnedMediaValue'),
                //   value: '€10,000(5x)',
                // },
                {
                  label: t('common.totalSales'),
                  value: isDummyUser
                    ? formatMoney(4600)
                    : formatMoney(campaign.totalSales ?? 0),
                },
              ];
              return (
                <div
                  className="bg-white px-[12px] pt-[12px] sm:px-[24px] sm:pt-[24px] f"
                  key={campaign._id}
                >
                  <div className="border border-[#E5E9EB] rounded-3xl p-[24px] flex flex-col gap-[24px]">
                    <div>
                      <div className="flex justify-between capitalize">
                        <span className="text-[#121111] text-lg font-normal">
                          {campaign.brandName}
                        </span>
                        <div className="flex gap-4 items-center">
                          {!isRegularBrandUser && (
                            <ToolTip
                              text={String(t('links.edit'))}
                              position="top"
                            >
                              <button
                                onClick={() =>
                                  navigate(`/edit-campaign/${campaign._id}`, {
                                    state: { from: 'active' },
                                  })
                                }
                                className="hover:bg-gray-100 rounded-full p-1"
                              >
                                <img
                                  src="assets/svgs/fi_edit.svg"
                                  className="h-[16px] w-[16px]"
                                  alt={String(
                                    t('campaign.navigation.editIcon'),
                                  )}
                                />
                              </button>
                            </ToolTip>
                          )}
                          <ToolTip
                            text={String(t('links.share'))}
                            position="top"
                          >
                            <button
                              onClick={() =>
                                handleShareModalOpen(campaign?._id)
                              }
                              className="hover:bg-gray-100 rounded-full p-1"
                            >
                              <img
                                src="assets/svgs/fi_share.svg"
                                className="h-[16px] w-[16px]"
                                alt={String(t('campaign.navigation.shareIcon'))}
                              />
                            </button>
                          </ToolTip>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex gap-[8px] items-center">
                          <img
                            src="/assets/fi_calendar.svg"
                            className="h-[16px] w-[16px]"
                            alt={String(t('campaign.navigation.calendarIcon'))}
                          />
                          <span className="text-[#000000B2]">
                            {startDate} -{' '}
                            {campaign.runCampaignTillMoneyEnd
                              ? t('common.untilBudgetEnds')
                              : endDate}
                          </span>
                        </div>
                        <div className="flex gap-[8px] items-center">
                          <img
                            src="/assets/fi_clock.svg"
                            className="h-[16px] w-[16px]"
                            alt={String(t('campaign.navigation.clockIcon'))}
                          />
                          <span className="text-[#000000B2]">{startTime}</span>
                        </div>
                        <div className=" bg-[#FFA5001A] rounded-full px-[10px]">
                          <span className="text-sm font-medium text-[#FFA500]">
                            {t('common.live')}
                          </span>
                        </div>
                      </div>{' '}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex gap-[32px]">
                        {data.map((item, index) => {
                          return (
                            <div key={index}>
                              {item.label === t('common.earnedMediaValue') ? (
                                <div className="text-[#9B919D] font-normal text-sm mb-2">
                                  {t('common.earnedMediaValue')}{' '}
                                  <ToolTip
                                    text={String(t('common.EMVTooltipDesc'))}
                                    position="top"
                                  >
                                    <span className="inline-block">ⓘ</span>
                                  </ToolTip>
                                </div>
                              ) : (
                                <div className="text-[#9B919D] font-normal text-sm mb-2">
                                  {item.label}
                                </div>
                              )}

                              <div className="flex gap-4 items-center">
                                <div
                                  className={`text-lg font-normal text-[#292828]`}
                                >
                                  {item.value}
                                </div>
                                {item.label ===
                                  t('common.remainingBalance') && (
                                  <div className="w-[133px]">
                                    <CustomProgressBar
                                      percentage={
                                        isDummyUser
                                          ? ((10000 - 1840) / 10000) * 100
                                          : campaign.campaignBudget
                                            ? (campaign.remainingCampaignBudget /
                                                campaign.campaignBudget) *
                                              100
                                            : 0
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {!isRegularBrandUser && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (campaign._id) {
                              pauseActiveCampaign(campaign._id);
                            }
                          }}
                        >
                          {t('common.pause')}
                        </Button>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-[#9B919D] font-normal text-sm mb-2">
                          {t('common.platform')}
                        </div>
                        <div className="flex gap-4">
                          <div className="flex flex-row gap-3">
                            {platform === 'both' || platform === 'tiktok' ? (
                              <img
                                src="assets/PlateformTikTok.svg"
                                className="w-[24px] h-[24px]"
                              />
                            ) : null}

                            {platform === 'both' || platform === 'instagram' ? (
                              <img
                                src="assets/PlateformInsta.svg"
                                className="w-[24px] h-[24px]"
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div>
                        <ToolTip
                          text={String(t('links.details'))}
                          position="top"
                        >
                          <IconButton
                            rel="noopener noreferrer"
                            onClick={() =>
                              navigate(`/campaign/${campaign._id}`)
                            }
                            src="assets/fi_full_right_arrow.svg"
                            className="px-2 py-2"
                          />
                        </ToolTip>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )
        ) : viewMode === 'paused' ? (
          <PausedCampaign />
        ) : (
          <DraftCampaign />
        )}
        {isLoading && (
          <div className="flex justify-center items-center py-4">
            <Loading
              centeredToParent={true}
              style={{
                top: 'calc(50% + 60px)',
              }}
            />
          </div>
        )}

        {hasNextPage && !isLoading && <div ref={sentinelRef} className="h-1" />}

        {isShareModalOpen && (
          <Model isOpen={isShareModalOpen} onClose={handleShareModalClose}>
            <LinkShareCard
              handleShareModalClose={handleShareModalClose}
              link={copyLink}
            />
          </Model>
        )}
      </div>
      {isInfiniteLoading && (
        <div className="">
          <Loading className="!top-[97%] !left-[60%]" />
        </div>
      )}
    </>
  );
};

export default ActiveCampaign;
